import React, { useState } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, Theme, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import mache, { colors } from '@skyslope/mache';
import Typography from '@material-ui/core/Typography';
import { IS_IPAD } from '../../common/constants';
import DialogActions from '@material-ui/core/DialogActions';
import ChooseStyle from './ChooseStyle';
import DrawSignature from './DrawSignature';
import { ISigner, ISignatureSettings } from '../../store/types';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Checkbox from '@material-ui/core/Checkbox';
import HelpIcon from '@material-ui/icons/Help';
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';
import { checkIfSignerHasSettingsSaved } from '../../common/utilities';

const macheTheme = createMuiTheme(mache);

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: 40,
    width: 'calc(100% - 80px)',
  },
  paperFullScreen: {
    padding: 24,
    width: 'calc(100% - 48px)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      fontSize: 24,
    },
  },
  content: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: colors.grey[100],
    padding: theme.spacing(4),
  },
  terms: {
    margin: '24px 24px 0px',
    color: colors.grey[800],
    fontSize: '15px',
    textAlign: 'center',
    width: '100%',
  },
  bottomTerms: {
    margin: '10px 0px 30px',
    color: colors.grey[800],
    fontSize: '15px',
    textAlign: 'center',
    width: '100%',
  },
  isMeSignerTerms: {
    margin: '24px 0px 0px',
    color: colors.grey[800],
    fontSize: '15px',
    textAlign: 'center',
    alignSelf: 'flex-start'
  },
  isMeBottomTerms: {
    margin: '10px 0px 30px 0px',
    color: colors.grey[800],
    fontSize: '15px',
    textAlign: 'center',
    alignSelf: 'flex-start',
  },
  saveSignature: {
    margin: '10px',
    color: colors.grey[800],
    fontSize: '15px',
    textAlign: 'center',
    alignSelf: 'flex-start'
  },
  buttonWrapper: {
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'column',
  },
  isMeSignerActions: {
    flexDirection: 'column',
    padding: '8px 8px 8px 0px'
  },
  tabs: {
    marginTop: 24,
  },
  tabsContainer: {
    borderBottom: `2px solid ${colors.blue[50]}`,
  },
  tab: {
    textTransform: 'none',
    color: colors.grey[500],
    minWidth: 0,
    padding: '0 16px',
    opacity: 1,
    '&:not($tabSelected):hover': {
      color: colors.grey[600],
    },
  },
  tabSelected: {
    color: colors.grey[800],
  },
  tooltip: {
    maxWidth: '250px',
    backgroundColor: colors.grey[900],
    borderRadius: '0px',
    padding: '30px',
    fontSize: '17px',
  },
  arrow: {
    color: colors.grey[900],
  },
  checkbox: {
    padding: '0px'
  }
}));

interface IProps {
  show: boolean;
  confirmSignatureModal: (signatureSettings: ISignatureSettings) => void;
  cancelSignatureModal: () => void;
  scrollToNextBlock: () => void;
  signatureAccepted: boolean;
  alreadySigned: boolean;
  signer: ISigner;
}

const SignatureSettingsModal = (props: IProps) => {
  const { show, confirmSignatureModal, cancelSignatureModal, scrollToNextBlock, alreadySigned, signer } = props;
  const classes = useStyles();
  const defaultName = [signer.firstName, signer.middleName, signer.lastName].filter(Boolean).join(' ');
  const [originalSignatureType, setOriginalSignatureType] = React.useState(signer.signingSettings?.signatureType);
  const isMeSigner = signer.title === "Me";
  const [saveSigningSettings, setSaveSigningSettings] = useState(false);
  const [signatureSettings, setSignatureSettings] = useState<ISignatureSettings>({
    signatureValue: '',
    initialsValue: '',
    signatureType: 'text',
    signatureFont: undefined,
  });
  const [selectedTab, setSelectedTab] = React.useState('choose-style');

  const fullScreen = IS_IPAD || useMediaQuery(macheTheme.breakpoints.down('md'));
  const getDisabledValue = () => {
    if (selectedTab === 'choose-style') {
      return signatureSettings?.signatureValue?.trim() && 
      signatureSettings?.initialsValue?.trim() && 
      signatureSettings?.signatureFont ? false : true;
    } else {
      return signatureSettings.signatureType === 'image' && 
      signatureSettings?.signatureValue?.trim() && signatureSettings?.initialsValue?.trim() &&
      signatureSettings?.signatureValue.startsWith('data:image/png;base64,') &&
      signatureSettings?.initialsValue.startsWith('data:image/png;base64,') ? false : true;
    }
  };
  const buttonsDisabled = getDisabledValue();
  
  React.useEffect(() => {
    if (props.signatureAccepted && !alreadySigned) {
      setTimeout(scrollToNextBlock, 0);
    }
  }, [show, props.signatureAccepted]);

  React.useEffect(() => {
    if (originalSignatureType === 'image') setSelectedTab('draw-signature');
  }, []);

  React.useEffect(() => {
    const { signingSettings } = signer;
    const isMeSigner = signer.title === "Me";
    if (isMeSigner && checkIfSignerHasSettingsSaved(signingSettings)) {
      if (signingSettings?.saveSigningSettings) {
        setSaveSigningSettings(true);
      }
      setSignatureSettings({
        signatureType: signingSettings?.signatureType,
        signatureValue: signingSettings?.signatureValue,
        initialsValue: signingSettings?.initialsValue,
        signatureFont: signingSettings?.signatureFont,
        saveSigningSettings: signingSettings?.saveSigningSettings,
      });
    }
  }, [signer]);

  const submit = () => {
    const finalSettings = { ...signatureSettings, saveSigningSettings };
    if (finalSettings.signatureType === 'text') {
      finalSettings.signatureValue = finalSettings.signatureValue?.trim().replace(/\s+/, ' ');
      finalSettings.initialsValue = finalSettings.initialsValue?.trim().replace(/\s+/, ' ');
    }
    confirmSignatureModal(finalSettings);
  };

  return (
    <ThemeProvider theme={macheTheme}>
      <Dialog
        open={show}
        onClose={() => cancelSignatureModal()}
        disableBackdropClick
        disableEscapeKeyDown
        fullScreen={fullScreen}
        scroll="body"
        classes={{ paper: fullScreen ? classes.paperFullScreen : classes.paper }}
        disablePortal
      >
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            Let's Set Your Signature
          </Typography>
          <IconButton onClick={() => cancelSignatureModal()} aria-label="close signature settings modal">
            <CloseIcon />
          </IconButton>
        </div>
        <Tabs
          value={selectedTab}
          indicatorColor="primary"
          onChange={(e, newValue) => setSelectedTab(newValue)}
          aria-label="select signature type"
          classes={{
            root: classes.tabs,
            flexContainer: classes.tabsContainer,
          }}
        >
          <Tab
            label="Choose Style"
            value="choose-style"
            aria-label="type in your signature and intials and choose a font"
            classes={{
              root: classes.tab,
              selected: classes.tabSelected,
            }}
          />
          <Tab
            label="Draw"
            value="draw-signature"
            aria-label="draw your own signature and initials using your input device"
            data-spec="draw-signature-tab"
            classes={{
              root: classes.tab,
              selected: classes.tabSelected,
            }}
          />
        </Tabs>
        <DialogContent className={classes.content}>
          {selectedTab === 'choose-style' && (
            <ChooseStyle
              signer={signer}
              signatureSettings={signatureSettings}
              setSignatureSettings={setSignatureSettings}
              defaultName={defaultName}
              data-spec="choose-style"
            />
          )}
          {selectedTab === 'draw-signature' && (
            <DrawSignature
              signatureSettings={signatureSettings}
              setSignatureSettings={setSignatureSettings}
              signer={signer}
              data-spec="draw-signature"
            />
          )}
        </DialogContent>
        <DialogActions className={isMeSigner ? classes.isMeSignerActions : classes.actions} disableSpacing>
          {isMeSigner ? <div
              style={{ 
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'flex-start'
              }}
            >
            <Checkbox
              data-spec="save-signature-checkbox"
              checked={saveSigningSettings}
              disabled={buttonsDisabled}
              onClick={() => setSaveSigningSettings(!saveSigningSettings)}
              color="primary"
              classes={{ root: classes.checkbox}}
              />
            <span className={classes.saveSignature}>Save this signature as my default signature for signing envelopes.</span>
            <Tooltip 
              title={
                <div>We’ll use this signature until<br />you decide to change it–<br />
                which you can do in the<br />Signing Actions menu <br />at any time.</div>
              }
              placement="bottom-start"
              TransitionComponent={Zoom}
              arrow
              classes={{ 
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
            >
              <HelpIcon style={{color: colors.grey[700], fontSize: '18px'}}/>
            </Tooltip>  
          </div> : ''}
          <div className={isMeSigner ? classes.isMeSignerTerms : classes.terms}>
            By selecting "Use This Signature" I am adopting my electronic signature,
          </div>
          <div className={isMeSigner ? classes.isMeBottomTerms : classes.bottomTerms}>
            which will have the same legal effect as my handwritten signature.
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={submit}
              disabled={buttonsDisabled}
              aria-label="use this signature button"
              onTouchEnd={submit}
              data-spec="submit-button"
            >
              Use This Signature
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default SignatureSettingsModal;
